import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Camera, CheckCircle2, Hand, XCircle } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, ErrorState, Identifier, LoadingState, useLocale,
} from '@hk/ui'
import {
  changeIssueState, fetchFaultDetail, formatCount, formatDateTime, formatMinutes,
  may, nameIn, needsReason, pluralise, translateError,
  type FaultDetailView, type FaultEvent, type IssueState,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-71 — ONE FAULT, IN FULL.  Sprint 64.

   ── WHY THE CARD IS NOT ENOUGH ────────────────────────────────────────────────
   The service board draws a fault as a small card on purpose: a category, a severity, a
   room number, a line of somebody's own words. That is the right size for scanning forty
   of them at seven in the morning, and it is the wrong size for the question that always
   follows the scan — **"why is 214 still off sale?"**

   Answering that needs four things a card cannot carry without becoming a screen:

     · WHEN, twice — when the phone recorded it and when it reached us, which on a replayed
       report from a basement are hours apart;
     · WHO, three times — who reported it, who took it, who closed it;
     · WHAT ELSE IS WRONG WITH THE SAME ROOM, which is #161's number shown BEFORE it bites:
       resolve this one while another blocking fault is open and the room does not come
       back, and the supervisor who has just promised it to the front desk finds out from
       the front desk;
     · EVERY STEP, with the reason given at each — because "who sat on this for three
       hours?" is exactly what a service complaint turns into.

   ── IT STAYS ON THE FAULT AFTER THE ACT ───────────────────────────────────────
   The board loses a fault the moment it is resolved, correctly. This screen does not: it
   refetches and redraws, so the closure, its reason and its author are on the screen at
   the moment they are most interesting. The same decision W-21 made in Sprint 62, and for
   the same reason — a screen that vanishes under somebody's hand is a screen that makes
   them press the thing again to check.

   ── TWO SENTENCES ABOUT THE PLACE AND NOT ONE ─────────────────────────────────
   `tookPlaceOutOfOrder` is HISTORY — which fault shut it — and `placeIsOutOfOrder` is the
   PRESENT. Collapsing them into "is this fault holding the room?" loses the case that
   actually happens: the fault is still open, the room is back on sale, because a different
   blocking fault was resolved and released it.
   ========================================================================= */

export function FaultDetail({
  issueId, propertyId, onClose,
}: {
  issueId: string
  propertyId: string
  onClose: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  const [view, setView] = useState<FaultDetailView>()
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState(true)
  const [asking, setAsking] = useState<IssueState>()
  const [reason, setReason] = useState('')
  const [refusal, setRefusal] = useState<string>()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setView(await fetchFaultDetail(issueId))
      setError(undefined)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [issueId])

  useEffect(() => { void load() }, [load])

  const act = async (toState: IssueState, why?: string) => {
    setRefusal(undefined)
    try {
      await changeIssueState(issueId, { toState, ...(why ? { reason: why } : {}) })
      setAsking(undefined); setReason('')
      /* **THE BOARD AND THE ROOM BOARD BOTH**, and the second one is the point of the
         whole feature: clearing a blocking fault puts a room back on sale, so a board
         still showing it out of order would be wrong within a second of this succeeding. */
      void queryClient.invalidateQueries({ queryKey: ['issues', propertyId] })
      void queryClient.invalidateQueries({ queryKey: ['room-board', propertyId] })
      await load()
    } catch (e) {
      /* **THE REFUSAL STAYS ON THIS SCREEN.** A permission refusal that closed the panel
         would leave somebody looking at a board wondering whether it worked. */
      setRefusal(translateError(e, locale).message)
    }
  }

  if (loading && !view) return <LoadingState />

  if (!view) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => void load()}
      />
    )
  }

  const blocking = view.severity === 'BLOCKING'
  const closed = view.state === 'RESOLVED' || view.state === 'CANCELLED'
  const canResolve = !blocking || may('fault.downgradeBlocking')

  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {/* **IT FLIPS IN ARABIC, AND THAT IS #168.** A back arrow is one of the very few
              glyphs that MUST mirror: it points the way out of the page, and in a
              right-to-left layout the way out is to the right. Three screens had been
              drawing a left-pointing arrow beside "العودة إلى اللوحة" since Sprint 51 —
              an arrow pointing INTO the page, next to the word "back". Caught by looking
              at the Arabic render rather than at the code, which is the only way this
              class of fault is ever caught. */}
          <ArrowLeft className="size-3.5 rtl:rotate-180" aria-hidden />
          {t.fdBack}
        </Button>

        <h1 className="text-lg font-semibold">
          <bdi>{nameIn(locale, view.categoryName, view.categoryNameAr)}</bdi>
        </h1>

        {/* THE PLACE IS AN IDENTIFIER WHEN IT IS A ROOM NUMBER — Western digits in both
            languages, because it is painted on the door and read aloud (D-01) — and
            ordinary bidirectional text when it is an area's name. */}
        {view.roomNumber
          ? <Identifier className="text-lg">{view.roomNumber}</Identifier>
          : <span className="text-lg"><bdi>{nameIn(locale, view.place, view.placeAr)}</bdi></span>}

        <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
          blocking ? 'bg-[#b91c1c] text-white' : 'bg-muted text-muted-foreground'}`}
        >
          {t.severities[view.severity]}
        </span>

        <span className="rounded border border-border px-1.5 py-0.5 text-[11px] font-medium
                         text-muted-foreground">
          {t.fdStates[view.state]}
        </span>

        <span className="ms-auto tnum text-sm text-muted-foreground">
          {fill(closed ? t.fdWasOpenFor : t.fdOpenFor, {
            duration: formatMinutes(view.minutesOpen, locale),
          })}
        </span>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <ReportCard view={view} />
          <PlaceCard view={view} />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title={t.fdHistory} />
            <CardBody>
              <History events={view.events} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t.fdActs} />
            <CardBody className="space-y-2">
              {closed ? (
                <p className="text-sm text-muted-foreground">{t.fdAlreadyClosed}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {view.state === 'OPEN' && (
                    <Button type="button" size="sm" variant="outline"
                            onClick={() => void act('ACKNOWLEDGED')}>
                      <Hand className="size-3.5" aria-hidden />
                      {t.fdAcknowledge}
                    </Button>
                  )}

                  <Button
                    type="button"
                    size="sm"
                    disabled={!canResolve}
                    title={canResolve ? undefined : t.issueNeedsOtherRole}
                    onClick={() => {
                      /* Ask for the reason BEFORE acting, so the box appears with the act
                         rather than after a refusal comes back. The server checks again
                         regardless — this is about not wasting somebody's time. */
                      if (blocking && needsReason('fault.downgradeBlocking')) setAsking('RESOLVED')
                      else void act('RESOLVED')
                    }}
                  >
                    <CheckCircle2 className="size-3.5" aria-hidden />
                    {t.resolve}
                  </Button>

                  {/* CANCELLING ALWAYS ASKS. The lifecycle demands a reason for it, so a
                      button that fired straight into a refusal would be a button that
                      exists to be refused. */}
                  <Button type="button" size="sm" variant="ghost"
                          onClick={() => setAsking('CANCELLED')}>
                    <XCircle className="size-3.5" aria-hidden />
                    {t.fdCancel}
                  </Button>
                </div>
              )}

              {refusal && (
                <p role="alert" className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
                  {refusal}
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {asking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div role="dialog" aria-modal="true"
               className="w-full max-w-sm rounded-hk border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">{t.reasonTitle}</h2>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {asking === 'CANCELLED' ? t.fdCancelReasonBody : t.reasonBody}
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              dir="auto"
              rows={3}
              aria-label={t.reasonTitle}
              className="mt-2.5 w-full rounded-hk border border-border bg-background p-2 text-[13px]"
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => { setAsking(undefined); setReason('') }}>
                {t.cancel}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={reason.trim() === ''}
                onClick={() => void act(asking, reason.trim())}
              >
                {t.save}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** **WHAT WAS REPORTED, BY WHOM, AND WHEN — TWICE.** */
function ReportCard({ view }: { view: FaultDetailView }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const reported = new Date(view.reportedAtUtc)
  const captured = view.capturedAtUtc ? new Date(view.capturedAtUtc) : null

  /* A MINUTE OF SLACK, the same rule the room's history panel uses and for the same
     reason: an online report writes both timestamps from one code path milliseconds
     apart, and a line saying "recorded at 09:14, reached us at 09:14" on every fault
     would be noise that buries the one case it exists for. */
  const replayed = captured !== null
    && reported.getTime() - captured.getTime() > 60_000

  return (
    <Card>
      <CardHeader title={t.fdWhatWasSaid} />
      <CardBody className="space-y-2">
        {/* **THEIR WORDS, NEVER TRANSLATED AND NEVER RE-FLOWED.** Running somebody's
            fault report through a translator is how "the lock sticks" becomes "the lock
            is stuck" and then becomes evidence in a dispute. */}
        {view.description
          ? (
            <p className="text-sm" dir="auto" style={{ unicodeBidi: 'isolate' }}>
              {view.description}
            </p>
          )
          : <p className="text-sm text-muted-foreground">{t.fdNoDescription}</p>}

        <p className="text-sm text-muted-foreground">
          {fill(t.fdReportedBy, {
            name: nameIn(locale, view.reportedByName, view.reportedByNameAr),
            when: formatDateTime(reported, locale),
          })}
        </p>

        {replayed && captured && (
          <p className="tnum text-[12px] text-muted-foreground">
            {fill(t.fdCapturedEarlier, {
              time: formatDateTime(captured, locale),
              recorded: formatDateTime(reported, locale),
            })}
          </p>
        )}

        <p className="text-sm text-muted-foreground">
          {view.acknowledgedAtUtc && view.acknowledgedByName
            ? fill(t.fdAcknowledgedBy, {
              name: nameIn(locale, view.acknowledgedByName, view.acknowledgedByNameAr),
              when: formatDateTime(new Date(view.acknowledgedAtUtc), locale),
            })
            : t.fdNobodyYet}
        </p>

        {view.closedAtUtc && view.closedByName && (
          <p className="text-sm text-muted-foreground">
            {fill(t.fdClosedBy, {
              name: nameIn(locale, view.closedByName, view.closedByNameAr),
              when: formatDateTime(new Date(view.closedAtUtc), locale),
            })}
          </p>
        )}

        {view.closureReason && (
          <p className="text-sm" dir="auto" style={{ unicodeBidi: 'isolate' }}>
            “{view.closureReason}”
          </p>
        )}

        {/* The photographs are on the board's card, which is where somebody looking at
            forty faults wants them. Here it is a COUNT and a pointer — the viewer itself
            is the board's, and duplicating it would be two dialogs to keep in step. */}
        {view.photoCount > 0 && (
          <p className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Camera className="size-3.5" aria-hidden />
            {t.issuePhotos}
            <span>{formatCount(view.photoCount, locale)}</span>
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/** **WHERE IT IS, WHETHER THAT PLACE IS SHUT, AND WHAT ELSE IS WRONG WITH IT.** */
function PlaceCard({ view }: { view: FaultDetailView }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <Card>
      <CardHeader title={t.fdPlace} />
      <CardBody className="space-y-2">
        <p className="text-sm font-medium">
          {view.roomNumber
            ? fill(t.fdRoomOnFloor, {
              number: view.roomNumber,
              floor: view.floorCode ?? '—',
            })
            : <bdi>{nameIn(locale, view.place, view.placeAr)}</bdi>}
        </p>

        {!view.roomNumber && (
          <p className="text-sm text-muted-foreground">{t.fdPublicArea}</p>
        )}

        {/* ── THE TWO SENTENCES, AND WHY THEY ARE TWO ──────────────────────────
            The first is history and the second is the present. A fault that shut a room
            somebody has since reopened says both, and neither one alone is the truth. */}
        {view.tookPlaceOutOfOrder && (
          <p className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
            {t.fdTookPlaceOut}
          </p>
        )}

        <p className="text-sm text-muted-foreground">
          {view.placeIsOutOfOrder
            ? (view.tookPlaceOutOfOrder ? t.fdPlaceStillShut : t.fdShutBySomethingElse)
            : t.fdPlaceBackInUse}
        </p>

        {/* **#161's NUMBER, BEFORE IT BITES.** A supervisor about to resolve this and
            promise the room to the front desk is entitled to know there is another leak
            on the same room — and, separately, whether that other leak is one that will
            keep the room shut. */}
        {view.otherOpenFaultsHere === 0
          ? <p className="text-sm text-muted-foreground">{t.fdNothingElseWrong}</p>
          : (
            <>
              <p className="text-sm text-muted-foreground">
                {pluralise(
                  t.fdOtherFaults, view.otherOpenFaultsHere, locale,
                  formatCount(view.otherOpenFaultsHere, locale))}
              </p>
              {view.otherBlockingFaultsHere > 0 && (
                <p className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
                  {pluralise(
                    t.fdOtherBlocking, view.otherBlockingFaultsHere, locale,
                    formatCount(view.otherBlockingFaultsHere, locale))}
                </p>
              )}
            </>
          )}
      </CardBody>
    </Card>
  )
}

/** **EVERY STEP, NEWEST FIRST**, because a history is read from the top. */
function History({ events }: { events: FaultEvent[] }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  if (events.length === 0) {
    /* **ITS OWN SENTENCE, NOT "nobody has taken it yet".** That one is about the
       acknowledgement and would be a wrong answer here — an empty history means nothing
       has been recorded, which on a fault imported from another system is a fact about
       the import rather than about anybody's diligence. */
    return <p className="text-[12px] text-muted-foreground">{t.fdNoHistory}</p>
  }

  return (
    <ol className="flex max-h-72 flex-col gap-1.5 overflow-y-auto pe-1">
      {events.map((e) => (
        <li key={e.serviceEventId}
            className="rounded-hk border border-border bg-card px-2.5 py-1.5 text-[12px]">
          <div className="flex flex-wrap items-baseline gap-x-1.5">
            {/* **THE RAISING IS "RAISED" AND NOT "— → OPEN".** The dash is the wire's way
                of saying "from nothing", and drawing it would be the product showing its
                own notation to a supervisor. */}
            <span className="font-medium">
              {e.fromState === '-' ? t.fdRaised : stateWord(t, e.toState)}
            </span>
            <span className="text-muted-foreground">
              <bdi>{e.actorName ? nameIn(locale, e.actorName, e.actorNameAr) : t.fdByNobody}</bdi>
            </span>
            <span className="ms-auto tnum text-[11px] text-muted-foreground">
              {formatDateTime(new Date(e.occurredAtUtc), locale)}
            </span>
          </div>

          {/* THE REASON IS THE POINT OF THE ROW IT IS ON. A cancellation without its
              reason is the record saying somebody did something and not why. */}
          {e.reason && (
            <div className="mt-0.5 text-[11.5px] text-muted-foreground" dir="auto">
              “{e.reason}”
            </div>
          )}
        </li>
      ))}
    </ol>
  )
}

/**
 * **A WORD THE DICTIONARY MIGHT NOT HAVE.** The history carries whatever the server wrote,
 * and a state added to the lifecycle before this dictionary catches up would otherwise
 * draw `undefined` on the screen. The code is not pretty and it is honest.
 */
function stateWord(t: (typeof STRINGS)['en'], wire: string): string {
  return (t.fdStates as Record<string, string>)[wire] ?? wire
}
